import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import Loader from '@/components/ui/Loader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import Title from '@/components/ui/Title';
import { Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectCurrentWorkspace } from '@/features/workspace/workspaceSlice';
import { fetchWorkspaceIntegrationMetadata } from '@/features/workspace/workspaceThunks';
import type { AppError } from '@/features/auth/types';
import type {
  WebinarIntegrationTrigger,
  WebinarIntegrationTriggerInput,
} from '@/features/webinar/types';
import {
  createAutomationTrigger,
  deleteAutomationTrigger,
  listAutomationTriggers,
  updateAutomationTrigger,
} from '@/features/webinar/webinarThunks';

const automationTriggerSchema = z.object({
  id: z.number().optional(),
  triggerEventType: z.enum(['JOINED'], {
    message: 'automationSection.validation.triggerRequired',
  }),
  actionType: z.enum(['ADD_TAG'], {
    message: 'automationSection.validation.consequenceRequired',
  }),
  tagId: z.string().min(1, 'automationSection.validation.tagRequired'),
});

type AutomationFormValues = z.infer<typeof automationTriggerSchema>;

const automationSectionSchema = z.object({
  rules: z.array(automationTriggerSchema),
});

type AutomationSectionFormValues = z.infer<typeof automationSectionSchema>;

function parseTagIdFromSettings(settingsJson: string): string {
  try {
    const parsed = JSON.parse(settingsJson) as { tagId?: unknown };
    if (parsed?.tagId != null) return String(parsed.tagId);
  } catch {
    // ignore invalid JSON
  }
  return '';
}

function filterActiveCampaignJoinedAddTag(triggers: WebinarIntegrationTrigger[]) {
  return triggers.filter(
    (t) =>
      t.providerType === 'ACTIVE_CAMPAIGN' &&
      t.triggerEventType === 'JOINED' &&
      t.actionType === 'ADD_TAG',
  );
}

function getAvailableTagsForRow(
  rowIndex: number,
  rules: { tagId?: string }[] | undefined,
  allTags: Array<{ id: string; label: string }>,
): Array<{ id: string; label: string }> {
  const list = rules ?? [];
  const currentTagId = list[rowIndex]?.tagId ?? '';
  const takenByOtherRows = new Set(
    list
      .map((r, i) => (i !== rowIndex && r?.tagId ? r.tagId : null))
      .filter((id): id is string => Boolean(id)),
  );
  return allTags.filter((tag) => !takenByOtherRows.has(tag.id) || tag.id === currentTagId);
}

const defaultFormValues = (): AutomationFormValues => ({
  id: undefined,
  triggerEventType: 'JOINED',
  actionType: 'ADD_TAG',
  tagId: '',
});

interface AutomationSectionProps {
  webinarId?: number;
}

export default function AutomationSection({ webinarId }: AutomationSectionProps) {
  const { t } = useTranslation('common');
  const { t: tCW } = useTranslation('createWebinar');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const workspaceId = workspace?.id;
  const tags = useAppSelector((s) => s.workspace.integrationMetadata);
  const integrationNotConnected = useAppSelector(
    (s) => s.workspace.integrationMetadataNotConnected,
  );
  const automationTriggersLoading = useAppSelector((s) => s.webinar.automationTriggersLoading);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [automationAccordionValue, setAutomationAccordionValue] = useState('');

  const schema = useMemo(() => automationSectionSchema, []);

  const form = useForm<AutomationSectionFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { rules: [] },
    mode: 'onChange',
  });

  const { control, formState, handleSubmit, reset, watch } = form;
  const watchedRules = watch('rules');
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rules',
    keyName: '_formKey',
  });

  const loadAndResetForm = useCallback(async () => {
    if (workspaceId == null || webinarId == null || Number.isNaN(webinarId)) {
      reset({ rules: [] });
      return;
    }
    try {
      const list = await dispatch(listAutomationTriggers({ workspaceId, webinarId })).unwrap();
      const existing = filterActiveCampaignJoinedAddTag(list);
      if (existing.length > 0) {
        reset({
          rules: existing.map((t) => ({
            id: t.id,
            triggerEventType: 'JOINED',
            actionType: 'ADD_TAG',
            tagId: parseTagIdFromSettings(t.settingsJson),
          })),
        });
      } else {
        reset({ rules: [] });
      }
    } catch {
      reset({ rules: [] });
    }
  }, [dispatch, reset, webinarId, workspaceId]);

  useEffect(() => {
    if (workspace?.id == null) return;
    void dispatch(
      fetchWorkspaceIntegrationMetadata({
        workspaceId: workspace.id,
        providerType: 'ACTIVE_CAMPAIGN',
        metadataType: 'TAGS',
      }),
    );
  }, [dispatch, workspace?.id]);

  useEffect(() => {
    void loadAndResetForm();
  }, [loadAndResetForm]);

  useEffect(() => {
    if (integrationNotConnected) setAutomationAccordionValue('');
  }, [integrationNotConnected]);

  const handleSubmitForm = async (data: AutomationSectionFormValues) => {
    if (workspaceId == null || webinarId == null || Number.isNaN(webinarId)) {
      return;
    }

    setIsSubmitting(true);
    try {
      const previous = await dispatch(listAutomationTriggers({ workspaceId, webinarId })).unwrap();
      const previousAc = filterActiveCampaignJoinedAddTag(previous);
      const prevById = new Map(previousAc.map((t) => [t.id, t]));
      const submittedIds = new Set(
        data.rules.map((r) => r.id).filter((id): id is number => id != null),
      );

      for (const t of previousAc) {
        if (!submittedIds.has(t.id)) {
          await dispatch(
            deleteAutomationTrigger({ workspaceId, webinarId, triggerId: t.id }),
          ).unwrap();
        }
      }

      for (const rule of data.rules) {
        const input: WebinarIntegrationTriggerInput = {
          providerType: 'ACTIVE_CAMPAIGN',
          triggerEventType: rule.triggerEventType,
          actionType: rule.actionType,
          settingsJson: JSON.stringify({ tagId: rule.tagId }),
          enabled: true,
        };

        if (rule.id != null) {
          const matching = prevById.get(rule.id);
          if (!matching) continue;
          const prevTag = parseTagIdFromSettings(matching.settingsJson);
          const changed =
            matching.triggerEventType !== input.triggerEventType ||
            matching.actionType !== input.actionType ||
            matching.providerType !== input.providerType ||
            prevTag !== rule.tagId ||
            matching.enabled !== (input.enabled ?? true);

          if (changed) {
            await dispatch(
              updateAutomationTrigger({
                workspaceId,
                webinarId,
                triggerId: rule.id,
                input,
              }),
            ).unwrap();
          }
        } else {
          await dispatch(createAutomationTrigger({ workspaceId, webinarId, input })).unwrap();
        }
      }

      await loadAndResetForm();
      toast.success(tCW('automationSection.saveSuccess'));
    } catch (error) {
      const appError = error as AppError;
      const errorMessage = appError?.message ? t(appError.message) : t('errors.generic');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formDisabled =
    integrationNotConnected || workspaceId == null || webinarId == null || Number.isNaN(webinarId);

  const automationRulesCountLabel = tCW('automationSection.rulesCount', { count: fields.length });

  return (
    <Accordion
      type="single"
      collapsible
      className="w-full"
      value={automationAccordionValue}
      onValueChange={(next) => {
        if (integrationNotConnected) return;
        setAutomationAccordionValue(next);
      }}
    >
      <AccordionItem value="item-0" className="accordion-page p-0">
        <AccordionTrigger
          content={integrationNotConnected ? undefined : automationRulesCountLabel}
          hideChevron={integrationNotConnected}
          trailingAction={
            integrationNotConnected ? (
              <div className="self-stretch pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    navigate('/integrations', { replace: true });
                  }}
                >
                  {tCW('automationSection.connect')}
                </Button>
              </div>
            ) : undefined
          }
        >
          <div className="flex items-center gap-4">
            <img
              src="/assets/active-campaign.png"
              alt="active-campaign"
              className="size-19 rounded-lg"
            />
            <div className="space-y-1">
              <Title level={2}>Active Campaign</Title>
              <p className="text-sm text-secondary">{tCW('automationSection.description')}</p>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className=" flex flex-col gap-4">
          {integrationNotConnected ? (
            <p className="text-sm text-destructive" role="alert">
              {tCW('automationSection.notConnected')}
            </p>
          ) : null}
          {webinarId == null || Number.isNaN(webinarId) ? (
            <p className="text-sm text-secondary" role="status">
              {tCW('automationSection.saveWebinarFirst')}
            </p>
          ) : null}
          <div className="flex flex-col gap-3">
            {fields.map((fieldRow, index) => (
              <div key={fieldRow._formKey} className="flex items-center gap-2">
                <div className="w-full">
                  <Controller
                    name={`rules.${index}.triggerEventType`}
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={formDisabled}
                      >
                        <SelectTrigger
                          label={tCW('automationSection.triggerLabel')}
                          className="w-full h-14"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="JOINED">
                            {tCW('automationSection.triggerJoined')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {formState.errors.rules?.[index]?.triggerEventType ? (
                    <p className="text-xs text-error mt-1">
                      {tCW(formState.errors.rules[index]?.triggerEventType?.message ?? '')}
                    </p>
                  ) : null}
                </div>

                <div className="w-full">
                  <Controller
                    name={`rules.${index}.actionType`}
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={formDisabled}
                      >
                        <SelectTrigger
                          label={tCW('automationSection.consequenceLabel')}
                          className="w-full h-14"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ADD_TAG">
                            {tCW('automationSection.actionAddTag')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {formState.errors.rules?.[index]?.actionType ? (
                    <p className="text-xs text-error mt-1">
                      {tCW(formState.errors.rules[index]?.actionType?.message ?? '')}
                    </p>
                  ) : null}
                </div>

                <div className="w-full">
                  <Controller
                    name={`rules.${index}.tagId`}
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={formDisabled}
                      >
                        <SelectTrigger label="Tag" className="w-full h-14">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {getAvailableTagsForRow(index, watchedRules, tags).map((tag) => (
                            <SelectItem key={tag.id} value={tag.id}>
                              {tag.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {formState.errors.rules?.[index]?.tagId ? (
                    <p className="text-xs text-error mt-1">
                      {tCW(formState.errors.rules[index]?.tagId?.message ?? '')}
                    </p>
                  ) : null}
                </div>

                <button
                  type="button"
                  className="bg-white rounded-lg py-3 px-2 border border-input cursor-pointer hover:bg-gray-50 h-14 flex items-center justify-center shrink-0"
                  disabled={formDisabled}
                  title={tCW('automationSection.deleteRule')}
                  onClick={() => remove(index)}
                >
                  <Trash2 className="size-6" />
                </button>
              </div>
            ))}
          </div>

          {fields.length < 3 ? (
            <button
              type="button"
              className="flex items-center gap-2 cursor-pointer"
              disabled={formDisabled}
              onClick={() => append(defaultFormValues())}
            >
              <Plus className="size-6" />
              <Label className="cursor-pointer">{tCW('automationSection.addTrigger')}</Label>
            </button>
          ) : null}

          <Button
            type="button"
            className="w-full"
            disabled={formDisabled || isSubmitting || automationTriggersLoading}
            onClick={handleSubmit(handleSubmitForm)}
          >
            {isSubmitting && <Loader />}
            {tCW('save')}
          </Button>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
